import { useMemo } from 'react';
import { useParams } from 'react-router';
import { EditOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { Button, Card, Divider, Flex, Typography } from 'antd';

import DisplayValue from 'components/DisplayValue/DisplayValue';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import SimpleGrid from 'components/SimpleGrid/SimpleGrid';

import useModalControls from 'hooks/useModalControls';
import { CUSTOMER_BANKRUPTCY_LEGAL_INFO } from 'queries/bankruptcy';
import { formatDate } from 'utilities/datesAndTimes';
import BankruptcyManagementModal from 'components/BankruptcyManagementModal/BankruptcyManagementModal';
import { getCardDisplayName } from 'utilities/helpers';
import useAgent from 'hooks/useAgent';

const { Title } = Typography;

const LOADING_SKELETON = {
  title: false,
  paragraph: { rows: 2, width: ['120px', '140px'] },
};

const BankruptcyLegalInfo = () => {
  const { customerId } = useParams();
  const agent = useAgent();
  const bankruptcyManagementModal = useModalControls();

  const bankruptcyLegalInfoQuery = useQuery(CUSTOMER_BANKRUPTCY_LEGAL_INFO, {
    variables: { customerId },
  });

  const accountsWithBankruptcyLegalInfo = useMemo(() => {
    const accountsWithBankruptcyLegalInfo = [];
    if (bankruptcyLegalInfoQuery.data) {
      const accounts =
        bankruptcyLegalInfoQuery.data.customer.creditAccounts || [];
      accounts.forEach((account) => {
        if (account.bankruptcyLegalInfo) {
          accountsWithBankruptcyLegalInfo.push(account);
        }
      });
    }
    return accountsWithBankruptcyLegalInfo;
  }, [bankruptcyLegalInfoQuery.data]);

  return (
    <div>
      <BankruptcyManagementModal {...bankruptcyManagementModal} />
      <Flex justify="space-between">
        <Title level={5}>Bankruptcy Legal Info</Title>
        {agent.isSuperUser ? (
          <Button
            data-testid="manage-bankruptcy-legal-info-button"
            type="link"
            icon={<EditOutlined style={{ fontSize: '1.5rem' }} />}
            onClick={bankruptcyManagementModal.show}
            size="small"
          />
        ) : null}
      </Flex>
      <Divider style={{ margin: '0 0 var(--spacing-sm)' }} />
      <QueryBoundary
        query={bankruptcyLegalInfoQuery}
        skeleton={LOADING_SKELETON}
      >
        {accountsWithBankruptcyLegalInfo.length ? (
          <SimpleGrid>
            {accountsWithBankruptcyLegalInfo.map((account) => {
              const bankruptcyLegalInfo = account.bankruptcyLegalInfo;

              return (
                <Card
                  title={getCardDisplayName(account)}
                  size="small"
                  style={{ gridColumn: 'span 6' }}
                  key={`bankruptcy-legal-info-${account.id}`}
                >
                  <SimpleGrid>
                    <DisplayValue
                      label="Stage"
                      style={{ gridColumn: 'span 4' }}
                    >
                      {bankruptcyLegalInfo.stage}
                    </DisplayValue>

                    <DisplayValue
                      label="Case Number"
                      style={{ gridColumn: 'span 4' }}
                    >
                      {bankruptcyLegalInfo.caseNumber}
                    </DisplayValue>

                    <DisplayValue
                      label="Chapter"
                      style={{ gridColumn: 'span 4' }}
                    >
                      {bankruptcyLegalInfo.chapter}
                    </DisplayValue>

                    <DisplayValue
                      label="Filing Date"
                      style={{ gridColumn: 'span 4' }}
                    >
                      {formatDate(bankruptcyLegalInfo.dateFiled)}
                    </DisplayValue>

                    <DisplayValue
                      label="Dismissal Date"
                      style={{ gridColumn: 'span 4' }}
                    >
                      {bankruptcyLegalInfo?.withdrawnDismissedDate
                        ? formatDate(bankruptcyLegalInfo.withdrawnDismissedDate)
                        : '-'}
                    </DisplayValue>
                  </SimpleGrid>
                </Card>
              );
            })}
          </SimpleGrid>
        ) : (
          <Typography.Paragraph type="secondary">
            Customer has no bankruptcy legal information
          </Typography.Paragraph>
        )}
      </QueryBoundary>
    </div>
  );
};

export default BankruptcyLegalInfo;
